// Client port of the server's selected-day focus (blueprint §15.9,
// backend/src/domain/workoutLog/homeDayItems.js). Day selection is local, so
// the focus of a newly selected day is derived here from the day's `items`,
// which the server built and classified. This module ranks and labels items;
// it never decides whether something is planned, extra, satisfied or startable.
// Pinned against the server by docs/contracts/homeSelectedDayFocus.json (v2).

export const FOCUS_ROLES = Object.freeze({
  STARTABLE_OCCURRENCE: "STARTABLE_OCCURRENCE",
  SATISFIED_OCCURRENCE: "SATISFIED_OCCURRENCE",
  EXECUTION: "EXECUTION",
  PREVIEW_OCCURRENCE: "PREVIEW_OCCURRENCE",
  REST: "REST",
});

// The primary item is the first item of the first role present.
export const FOCUS_ROLE_ORDER = Object.freeze([
  FOCUS_ROLES.STARTABLE_OCCURRENCE,
  FOCUS_ROLES.SATISFIED_OCCURRENCE,
  FOCUS_ROLES.EXECUTION,
  FOCUS_ROLES.PREVIEW_OCCURRENCE,
]);

const ROLE_ACTIONS = Object.freeze({
  [FOCUS_ROLES.STARTABLE_OCCURRENCE]: ["START_SESSION"],
  [FOCUS_ROLES.SATISFIED_OCCURRENCE]: ["DETAILS", "TRAIN_AGAIN"],
  [FOCUS_ROLES.EXECUTION]: ["DETAILS", "TRAIN_AGAIN"],
  [FOCUS_ROLES.PREVIEW_OCCURRENCE]: [],
  [FOCUS_ROLES.REST]: [],
});

export const REST_TITLE = "Rest / Recovery";
export const NO_PROGRAM_SUBTITLE = "No session planned";

export function getItemFocusRole(item) {
  if (item?.kind === "EXECUTION") return FOCUS_ROLES.EXECUTION;
  if (item?.kind !== "OCCURRENCE") return null;
  if (item.occurrence.startable) return FOCUS_ROLES.STARTABLE_OCCURRENCE;
  if (item.occurrence.status === "SATISFIED") return FOCUS_ROLES.SATISFIED_OCCURRENCE;
  return FOCUS_ROLES.PREVIEW_OCCURRENCE;
}

// Details and Train again need a real saved execution: a completed occurrence
// with no `executionId` has no action.
export function getItemActions(item) {
  const role = getItemFocusRole(item);
  if (role === FOCUS_ROLES.SATISFIED_OCCURRENCE && !item.occurrence.execution?.executionId) {
    return [];
  }
  return [...(ROLE_ACTIONS[role] || [])];
}

function describeItem(item) {
  return { role: getItemFocusRole(item), actions: getItemActions(item), item };
}

export function buildRestFocus(selectedDate, currentProgram) {
  return {
    date: selectedDate,
    type: "rest",
    title: REST_TITLE,
    subtitle: currentProgram?.name || NO_PROGRAM_SUBTITLE,
    showStartSession: false,
    primary: { role: FOCUS_ROLES.REST, actions: [], item: null },
    others: [],
  };
}

// items: one day's `items`, in the server's day order. `others` keeps the
// remaining items in that order.
export function buildSelectedDayFocus({ selectedDate, items = [], currentProgram = null } = {}) {
  const described = (items || []).map(describeItem);
  let primary = null;
  for (const role of FOCUS_ROLE_ORDER) {
    primary = described.find((entry) => entry.role === role) || null;
    if (primary) break;
  }
  if (!primary) return buildRestFocus(selectedDate, currentProgram);

  const execution = primary.item.kind === "EXECUTION";
  const { occurrence } = primary.item;
  return {
    date: selectedDate,
    type: execution ? "execution" : "session",
    title: execution ? primary.item.execution.title : occurrence.workoutName,
    subtitle: execution
      ? primary.item.execution.sourceLabel
      : `Workout ${occurrence.orderIndex} · Week ${occurrence.weekNumber}`,
    showStartSession: primary.role === FOCUS_ROLES.STARTABLE_OCCURRENCE,
    primary,
    others: described.filter((entry) => entry !== primary),
  };
}

// Every item of the selected day in viewing order: the canonical primary
// first, then the others in the server's day order. A rest day has none.
export function listFocusEntries(focus) {
  return focus?.primary?.item ? [focus.primary, ...(focus.others || [])] : [];
}

// Stable identity of one entry within its day.
export function focusEntryKey(entry) {
  const { item } = entry;
  return item.kind === "EXECUTION"
    ? `execution:${item.execution.executionId}`
    : `occurrence:${item.occurrence.cycleId}:${item.occurrence.occurrenceKey}`;
}
